// Detects that a newer build of vamos-app has been deployed while this window is open.
//
// The build bakes REACT_APP_VERSION (the git SHA, from CI) into the bundle, and
// scripts/write-version.js publishes the same value at /version.json (served no-store).
// We poll that file and compare. Same-origin, so no CORS and no API change is involved.
//
// See vamos-docs/docs/PWA-and-Versioning-Analysis.md.

export const VERSION_URL = '/version.json'
export const POLL_INTERVAL_MS = 5 * 60 * 1000
// Focus and online events can fire in bursts; don't re-check more often than this.
export const MIN_CHECK_GAP_MS = 30 * 1000

export const CURRENT_VERSION = process.env.REACT_APP_VERSION || 'local'

export function isVersionCheckEnabled(env = process.env) {
  return (
    env.NODE_ENV === 'production' && !!env.REACT_APP_VERSION && env.REACT_APP_VERSION !== 'local'
  )
}

/**
 * Parses a /version.json body. Anything that isn't the expected JSON returns null, which
 * means "no update". That covers nginx's SPA fallback answering with index.html.
 */
export function parseVersionPayload(text) {
  try {
    const data = JSON.parse(text)
    if (data && typeof data.version === 'string' && data.version) {
      return { version: data.version, critical: data.critical === true }
    }
  } catch (e) {
    // not JSON
  }
  return null
}

export async function fetchRemoteVersion(fetchImpl = fetch) {
  try {
    const res = await fetchImpl(`${VERSION_URL}?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
    if (!res || !res.ok) return null
    return parseVersionPayload(await res.text())
  } catch (e) {
    return null
  }
}

/** Pure comparison used by the store; `remote` is a parseVersionPayload result or null. */
export function compareVersions(current, remote) {
  if (!remote || remote.version === current) return { updateAvailable: false, critical: false }
  return { updateAvailable: true, critical: remote.critical }
}

// --- Automatic-reload guard --------------------------------------------------------------
// If something between us and nginx ever caches /version.json (or index.html), a reload can
// land on a build that still disagrees with the remote version, and every navigation would
// reload again. So an *automatic* reload towards a given remote version happens at most once
// per AUTO_RELOAD_WINDOW_MS; after that the banner stays, but only the user's click reloads.
export const AUTO_RELOAD_KEY = 'vamos_version_reload'
export const AUTO_RELOAD_WINDOW_MS = 10 * 60 * 1000

export function claimAutoReload(remoteVersion, storage = window.sessionStorage, now = Date.now()) {
  try {
    const last = JSON.parse(storage.getItem(AUTO_RELOAD_KEY) || 'null')
    if (last && last.version === remoteVersion && now - last.at < AUTO_RELOAD_WINDOW_MS) {
      return false
    }
    storage.setItem(AUTO_RELOAD_KEY, JSON.stringify({ version: remoteVersion, at: now }))
    return true
  } catch (e) {
    // Without storage the guard can't work, so never reload automatically.
    return false
  }
}

// --- Shared store -------------------------------------------------------------------------
// One poller for the whole page. App.js swaps between several router trees (logged out,
// must-change-password, logged in), and each mounts a VersionWatcher; they all read this.

let state = { updateAvailable: false, critical: false, version: null }
const listeners = new Set()
let started = false
let lastCheckAt = 0

function setState(next) {
  state = next
  listeners.forEach((listener) => listener())
}

export function getVersionState() {
  return state
}

export function subscribeVersionState(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export async function checkForUpdate({ force = false, fetchImpl, current = CURRENT_VERSION } = {}) {
  const now = Date.now()
  if (!force && now - lastCheckAt < MIN_CHECK_GAP_MS) return state
  lastCheckAt = now
  const remote = await fetchRemoteVersion(fetchImpl)
  const result = compareVersions(current, remote)
  // Once an update is seen, keep it: a later failed fetch must not hide the banner.
  if (result.updateAvailable) {
    setState({ updateAvailable: true, critical: result.critical, version: remote.version })
  }
  return state
}

export function startVersionPolling() {
  if (started || !isVersionCheckEnabled()) return
  started = true

  checkForUpdate({ force: true })
  setInterval(() => {
    if (document.visibilityState === 'visible') checkForUpdate()
  }, POLL_INTERVAL_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForUpdate()
  })
  window.addEventListener('online', () => checkForUpdate())
}

// Test-only: reset module state between tests.
export function __resetVersionStateForTests() {
  state = { updateAvailable: false, critical: false, version: null }
  listeners.clear()
  started = false
  lastCheckAt = 0
}
